import { FC, useState } from 'react';
import paulinaAvatar from '@assets/images/paulina-avatar.jpg';
import { V4 } from '@components/v4';
import { AGENT_CHAT_DEACTIVATE_CONFIRM } from '@utils/featureRollout/strings';
import { paulinaTrack } from './paulinaTrack';

/**
 * Header del chat de Paulina (avatar + nombre + borrar + cerrar + overflow), compartido por
 * el panel desktop y el bottom-sheet móvil. Emoji nativo prohibido en chrome de UI
 * (DESIGN.md) → iconos SVG.
 *
 * Overflow (⋮): SOLO se monta cuando `onDeactivate` viene (can_revert + root efectivo, lo
 * decide el host vía usePaulinaVersionControls). Abre un menú discreto con "Desactivar Paulina"
 * → confirm de 2 pasos → ejecuta el revert del rollout (Paulina deja de montarse) + emite
 * `pau_desactivada { motivo:'' }` (el motivo se recoge DESPUÉS en el prompt de feedback). El
 * prompt de motivos lo dispara la propia op de desactivar (signal de módulo).
 */

interface PaulinaChatHeaderProps {
  busy: boolean;
  emptyConversation: boolean;
  onClear: () => void;
  onClose: () => void;
  /** Desactivar Paulina (revert del rollout). Si no viene, el overflow no se monta. */
  onDeactivate?: () => void;
}

export const PaulinaChatHeader: FC<PaulinaChatHeaderProps> = ({
  busy,
  emptyConversation,
  onClear,
  onClose,
  onDeactivate,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const closeMenu = () => {
    setMenuOpen(false);
    setConfirming(false);
  };

  const handleConfirmDeactivate = () => {
    closeMenu();
    // El motivo se recoge DESPUÉS (prompt de feedback). Aquí va vacío.
    paulinaTrack('pau_desactivada', { motivo: '' });
    onDeactivate?.();
  };

  return (
    <div className="relative flex items-center justify-between bg-[#253746] px-4 py-3 text-[#ffffff]">
      <div className="flex items-center gap-3">
        <img
          src={paulinaAvatar}
          alt="Paulina"
          className="h-9 w-9 rounded-full border-2 border-[#4298B5] object-cover"
        />
        <div>
          <p className="font-semibold leading-tight">Paulina</p>
          <p className="text-xs opacity-80">Asistente de Plick</p>
        </div>
      </div>
      <div className="flex items-center gap-1">
        <button
          type="button"
          aria-label="Borrar conversación"
          title="Borrar conversación"
          className="rounded p-1.5 opacity-80 hover:opacity-100 disabled:opacity-40"
          disabled={busy || emptyConversation}
          onClick={onClear}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2m2 0v12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V7"
              stroke={V4.white}
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path d="M10 11v6M14 11v6" stroke={V4.white} strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </button>
        {onDeactivate && (
          <button
            type="button"
            aria-label="Más opciones"
            title="Más opciones"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            className="rounded p-1.5 opacity-80 hover:opacity-100"
            onClick={() => setMenuOpen((v) => !v)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="12" cy="5" r="1.6" fill={V4.white} />
              <circle cx="12" cy="12" r="1.6" fill={V4.white} />
              <circle cx="12" cy="19" r="1.6" fill={V4.white} />
            </svg>
          </button>
        )}
        <button
          type="button"
          aria-label="Cerrar chat"
          className="rounded p-1.5 opacity-80 hover:opacity-100"
          onClick={onClose}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" stroke={V4.white} strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      {onDeactivate && menuOpen && (
        <>
          {/* Click-fuera para cerrar el menú (no atrapamos foco: menú discreto). */}
          <button
            type="button"
            aria-hidden="true"
            tabIndex={-1}
            onClick={closeMenu}
            className="fixed inset-0 cursor-default"
            style={{ background: 'transparent', border: 0, zIndex: 1 }}
          />
          <div
            role="menu"
            aria-label="Más opciones"
            className="absolute right-3 top-[52px] z-[2] w-[260px] rounded-lg border border-[#e2e8f0] bg-[#ffffff] p-2 text-[#253746] shadow-xl"
          >
            {!confirming ? (
              <button
                type="button"
                role="menuitem"
                onClick={() => setConfirming(true)}
                className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm font-semibold hover:bg-[#f2f6f8]"
                style={{ background: 'transparent', border: 0, cursor: 'pointer', color: V4.grayUltraDark }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M18.36 6.64A9 9 0 1 1 5.64 6.64M12 2v10"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Desactivar Paulina
              </button>
            ) : (
              <div role="alertdialog" aria-label={AGENT_CHAT_DEACTIVATE_CONFIRM.title} className="px-1.5 py-1">
                <p className="text-sm font-bold" style={{ color: V4.primary }}>
                  {AGENT_CHAT_DEACTIVATE_CONFIRM.title}
                </p>
                <p className="mt-1 text-xs leading-relaxed" style={{ color: V4.grayUltraDark }}>
                  {AGENT_CHAT_DEACTIVATE_CONFIRM.body}
                </p>
                <div className="mt-3 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setConfirming(false)}
                    className="rounded-md px-3 py-1.5 text-xs font-semibold"
                    style={{
                      background: 'transparent',
                      border: `1px solid ${V4.grayLight}`,
                      color: V4.primary,
                      cursor: 'pointer',
                    }}
                  >
                    {AGENT_CHAT_DEACTIVATE_CONFIRM.cancel}
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmDeactivate}
                    className="rounded-md px-3 py-1.5 text-xs font-bold"
                    style={{ background: V4.primary, color: V4.white, border: 0, cursor: 'pointer' }}
                  >
                    {AGENT_CHAT_DEACTIVATE_CONFIRM.confirm}
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
